import { ArrowRightLeft } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ZMENA_STAVY, type ZmenaStav } from '@helfr/domain';
import { api, hlaskaZChyby } from '../../api/client.js';
import type { Zakazka, ZmenaSeznam } from '../../api/types.js';
import { formatKc } from '../../lib/format.js';
import { useVybranaZakazka } from '../../lib/globalniZakazka.js';
import { ZMENA_STAV_BARVA, ZMENA_STAV_LABEL } from '../../lib/labels.js';
import {
  Badge,
  Chyba,
  EmptyState,
  Karta,
  Napoveda,
  Select,
  SkeletonSeznam,
  td,
  th,
} from '../../components/ui.js';
import { ProcesViceprace } from '../../components/ProcesViceprace.js';

/**
 * Legenda tabulek Změn/Rekapitulace — text nezmizel, jen z OČÍ: schovaný za „?"
 * u nadpisu sekce (blueprint 16 — žargon P?/A!/ZL/SOD/CN nikdy natrvalo na
 * plátně). Pod tabulkou zůstává jen drobný řádek „ceny bez DPH".
 */
export function LegendaZmen() {
  return (
    <Napoveda titulek="Vysvětlivky tabulky">
      <p>
        <strong>Provedeno</strong> = práce v terénu proběhla: ano · ne ·{' '}
        <span className="font-bold text-error-text">na riziko! (provedeno bez podpisu)</span>.{' '}
        <strong>„?"</strong> = strana ještě není oceněná (nikdy neukazujeme 0). Zkratky:{' '}
        <strong>ZL</strong> změnový list · <strong>SOD</strong> smlouva o dílo · <strong>CN</strong>{' '}
        cenová nabídka.
      </p>
    </Napoveda>
  );
}

/** Provedeno v tabulkách: A / N / A! (na riziko) — A! vždy červeně. */
export function ProvedenoZnak({ provedeno }: { provedeno: 'A' | 'N' | 'A!' }) {
  // Slova místo kódů A/N/A! — tabulka má odpovídat bez luštění šifer.
  if (provedeno === 'A!') {
    return (
      <span
        className="font-bold whitespace-nowrap text-error-text"
        title="provedeno na riziko — bez podepsaného ZL / schválení"
      >
        na riziko!
      </span>
    );
  }
  return <span>{provedeno === 'A' ? 'ano' : 'ne'}</span>;
}

/**
 * Seznam Změn zakázky — stav, obě strany, marže (null = „?", nikdy 0);
 * proklik na detail (obrazovka 3). Filtry: zakázka, stav, jen neúplné.
 */
export function ZmenyPage() {
  const navigate = useNavigate();
  const [stav, setStav] = useState<ZmenaStav | ''>('');
  const [jenNeuplne, setJenNeuplne] = useState(false);

  const { data: zakazky, isLoading: nacitamZakazky } = useQuery({
    queryKey: ['zakazky'],
    queryFn: () => api.get<Zakazka[]>('/api/projects'),
  });
  // Zakázka z globálního přepínače v hlavičce (B1.2) — žádný vlastní select.
  const { vybrana, vybranaId } = useVybranaZakazka(zakazky);

  const params = new URLSearchParams({ zakazkaId: vybranaId });
  if (stav) params.set('stav', stav);
  if (jenNeuplne) params.set('jenNeuplne', 'true');

  const {
    data: zmeny,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['zmeny', params.toString()],
    queryFn: () => api.get<ZmenaSeznam[]>(`/api/zmeny?${params.toString()}`),
    enabled: !!vybranaId,
  });

  return (
    <>
      <ProcesViceprace krok={2} />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold">
            Změny
            {vybrana ? (
              <span className="ml-2 text-lg font-normal text-text-secondary">
                — {vybrana.nazev}
              </span>
            ) : null}
          </h1>
          {/* Definice „vícepráce a méněpráce" + vysvětlivky tabulky on-demand za „?". */}
          <Napoveda titulek="Co jsou Změny">
            <p>
              Změny jsou <strong>vícepráce a méněpráce</strong> — práce navíc nebo naopak méně
              oproti smlouvě. U každé sledujeme, kolik na ní vyděláme (marže).
            </p>
          </Napoveda>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm font-semibold text-text-secondary">
            Stav
            <Select
              className="mt-1 !min-h-11 w-44"
              value={stav}
              onChange={(e) => setStav(e.target.value as ZmenaStav | '')}
            >
              <option value="">vše</option>
              {ZMENA_STAVY.map((s) => (
                <option key={s} value={s}>
                  {ZMENA_STAV_LABEL[s]}
                </option>
              ))}
            </Select>
          </label>
          <label className="flex min-h-11 items-center gap-2 text-sm font-semibold text-text-secondary">
            <input
              type="checkbox"
              className="size-5"
              checked={jenNeuplne}
              onChange={(e) => setJenNeuplne(e.target.checked)}
            />
            jen bez ocenění („?")
          </label>
        </div>
      </div>

      {(nacitamZakazky || isLoading) && <SkeletonSeznam radku={4} />}
      {error ? <Chyba>{hlaskaZChyby(error)}</Chyba> : null}
      {zakazky && zakazky.length === 0 && (
        <p className="text-text-secondary">Zatím žádná zakázka — Změny se vážou na zakázku.</p>
      )}

      {zmeny && zmeny.length === 0 && (
        <EmptyState
          ikona={<ArrowRightLeft size={22} strokeWidth={1.75} />}
          titulek="Zatím žádné změny"
          popis="Změna vzniká v sekci Nálezy rozhodnutím „je to vícepráce“ — tady pak sledujete její marži."
        />
      )}

      {zmeny && zmeny.length > 0 && (
        <Karta>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className={th}>Č.</th>
                  <th className={th}>Změna</th>
                  <th className={th}>Objekt</th>
                  <th className={th}>Stav</th>
                  <th
                    className={`${th} text-center`}
                    title="provedeno: A ano / N ne / A! na riziko"
                  >
                    Provedeno
                  </th>
                  <th className={`${th} text-right`}>Výnos</th>
                  <th className={`${th} text-right`}>Náklad</th>
                  <th className={`${th} text-right`}>Marže</th>
                </tr>
              </thead>
              <tbody>
                {zmeny.map((z) => {
                  const ztratova = z.marze != null && z.marze.startsWith('-');
                  return (
                    <tr
                      key={z.id}
                      className={`cursor-pointer ${z.stav === 'sporna' ? 'bg-sporna-bg' : 'hover:bg-bg'}`}
                      onClick={() => void navigate(`/zmeny/${z.id}`)}
                    >
                      <td className={`${td} font-semibold whitespace-nowrap`}>
                        <Link
                          to={`/zmeny/${z.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-primary underline underline-offset-2"
                        >
                          {z.cislo ?? z.id}
                        </Link>
                      </td>
                      <td className={td}>{z.popis}</td>
                      <td className={td}>{z.objekt?.kod ?? '—'}</td>
                      <td className={td}>
                        <Badge barva={ZMENA_STAV_BARVA[z.stav]}>{ZMENA_STAV_LABEL[z.stav]}</Badge>
                      </td>
                      <td className={`${td} text-center`}>
                        <ProvedenoZnak provedeno={z.provedeno} />
                      </td>
                      <td className={`${td} text-right whitespace-nowrap`}>
                        {formatKc(z.vynos)}
                        {z.spornyVynos ? '?' : ''}
                      </td>
                      <td className={`${td} text-right whitespace-nowrap`}>{formatKc(z.naklad)}</td>
                      <td
                        className={`${td} text-right font-semibold whitespace-nowrap ${
                          z.marze == null
                            ? 'text-info-text'
                            : ztratova
                              ? 'text-error-text'
                              : 'text-success-text'
                        }`}
                      >
                        {formatKc(z.marze)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Ceny bez DPH jednou drobně; vysvětlivky zkratek za „?" (blueprint 16). */}
          <div className="mt-3 flex items-center gap-2 text-sm text-text-secondary">
            <span>Částky netto, ceny bez DPH.</span>
            <LegendaZmen />
          </div>
        </Karta>
      )}
    </>
  );
}
